import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './HomeFeedback.css';

export function useHomeFeedback() {
  const [dialog, setDialog] = useState(null);
  const queue = useRef([]);
  const active = useRef(null);
  const enqueue = useCallback((message, options = {}) => new Promise(resolve => {
    const entry = { message, ...options, resolve };
    if (active.current) queue.current.push(entry);
    else { active.current = entry; setDialog(entry); }
  }), []);
  const notify = useCallback((message, options) => { void enqueue(message, options); }, [enqueue]);
  const confirm = useCallback((message, options) => enqueue(message, { ...options, confirmation: true }), [enqueue]);
  const prompt = useCallback(message => enqueue(message, { input: true, confirmation: true }), [enqueue]);
  const dismiss = useCallback(value => {
    active.current?.resolve(active.current?.input && value === false ? null : value);
    active.current = queue.current.shift() || null;
    setDialog(active.current);
  }, []);
  useEffect(() => () => {
    active.current?.resolve(false);
    queue.current.forEach(entry => entry.resolve(false));
    active.current = null;
    queue.current = [];
  }, []);
  return { dialog, notify, confirm, prompt, dismiss };
}

export default function HomeFeedback({ dialog, onDismiss, className = '' }) {
  const { t } = useTranslation('home');
  const panel = useRef(null);
  const [input, setInput] = useState('');
  useEffect(() => {
    if (!dialog) return undefined;
    setInput('');
    const previous = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector('textarea, button')?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        onDismiss(false);
      }
      if (event.key === 'Tab') {
        const buttons = panel.current?.querySelectorAll('textarea, button');
        if (!buttons?.length) return;
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKey, true);
      if (previous?.isConnected) previous.focus();
    };
  }, [dialog, onDismiss]);
  if (!dialog) return null;
  const title = dialog.title || (dialog.confirmation
    ? t('feedback.confirmTitle', 'Please confirm') : t('feedback.noticeTitle', 'Update'));
  return createPortal(
    <div className={`home-feedback-backdrop ${className}`}>
      <section ref={panel} className="home-feedback-panel" role="alertdialog" aria-modal="true" aria-labelledby="home-feedback-title" aria-describedby="home-feedback-message">
        <div className={`home-feedback-icon ${dialog.tone || ''}`} aria-hidden="true">
          <i className={`fa-solid ${dialog.confirmation ? 'fa-circle-question' : dialog.tone === 'success' ? 'fa-circle-check' : 'fa-circle-info'}`} />
        </div>
        <h2 id="home-feedback-title">{title}</h2>
        <p id="home-feedback-message">{dialog.message}</p>
        {dialog.input && <textarea className="home-feedback-input" aria-label={dialog.message} value={input} onChange={event => setInput(event.target.value)} rows={4} />}
        <div className="home-feedback-actions">
          {dialog.confirmation && <button type="button" className="home-feedback-secondary" onClick={() => onDismiss(false)}>{t('feedback.cancel', 'Cancel')}</button>}
          <button type="button" className="home-feedback-primary" onClick={() => onDismiss(dialog.input ? input : true)}>
            {dialog.confirmLabel || (dialog.confirmation ? t('feedback.continue', 'Continue') : t('feedback.done', 'Got it'))}
          </button>
        </div>
      </section>
    </div>, document.body
  );
}
